import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { ActionModal, DialogPosition, Button } from '@ecdlink/ui';
import { InfantDto, usePrevious } from '@ecdlink/core';
import { useAppDispatch } from '@/store';
import {
  CmsVisitDataInputModelInput,
  CmsVisitSectionInput,
  InputMaybe,
  VisitDataStatusFilterInput,
} from '@ecdlink/graphql';
import { visitActions, visitThunkActions } from '@/store/visit';
import { useThunkFetchCall } from '@/hooks/useThunkFetchCall';
import { VisitActions } from '@/store/visit/visit.actions';
import { useRequestResponseDialog } from '@/hooks/useRequestResponseDialog';
import { referralThunkActions } from '@/store/referral';
import { ReferralActions } from '@/store/referral/referral.actions';
import { GrowthMonitoring } from './pillar-1-steps';
import { useLocation, useParams } from 'react-router';
import { InfantProfileParams } from '../../../infant-profile.types';
import { useDialog } from '@ecdlink/core';
import ROUTES from '@/routes/routes';
import { useHistory } from 'react-router';
import { DocumentActions } from '@/store/document/document.actions';
import { currentActivityKey } from '..';
import { InfantModelInput } from '@/../../../packages/graphql/lib';
import { infantActions, infantThunkActions } from '@/store/infant';
import { ButtonProps } from '@ecdlink/ui/lib/components/button/button.types';
import { useSelector } from 'react-redux';
import {
  notificationActions,
  notificationsSelectors,
} from '@store/notifications';
import { notificationTagConfig } from '@/constants/notifications';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { getInfantStatusInfo } from '@/utils/infant/infantVisitStatus.utils';
import { transformToVisitDataDtos } from '@/utils/visits/visitDataTransformer.util';
import {
  getVisitStepKey,
  mergeVisitSectionQuestions,
  useVisitedVisitSteps,
} from '@/utils/visits/visit-step-state.util';

export interface Question {
  question: string;
  answer:
    | string
    | string[]
    | boolean
    | boolean[]
    | (string | number | undefined)[]
    | undefined;
}

export interface SectionQuestions {
  visitSection: string;
  questions: Question[];
}

export interface ViewEditState {
  editView?: true;
}

type Risk = 0 | 1 | 2;
export interface DynamicFormProps {
  name?: string;
  infant?: InfantDto;
  currentStep?: number;
  isTipPage?: boolean;
  steps?: any[]; // TODO: add type
  sectionQuestions?: SectionQuestions[];
  growthMonitoring?: GrowthMonitoring;
  setIsTip?: (value: boolean) => void;
  setSectionQuestions?: (value?: SectionQuestions[]) => void;
  setReferralsInput?: (value?: VisitDataStatusFilterInput[]) => void;
  setEnableButton?: (value: boolean) => void;
  setButtonProperties?: (value?: ButtonProps) => void;
  setExtraButtons?: (buttons?: JSX.Element[]) => void;
  onNextStep?: () => void;
  onPreviousStep?: () => void;
  onClose?: () => void;
  onSubmit?: () => void;
  setGrowthMonitoring?: (value: GrowthMonitoring) => void;
  setRisk?: (value: number) => void;
  setInfantInput?: (value?: InfantModelInput) => void;
}

export const DynamicForm = ({
  name,
  infant,
  currentStep,
  steps,
  isTipPage,
  setSectionQuestions: setSectionQuestionsForm,
  onNextStep,
  setIsTip,
  onClose,
}: DynamicFormProps) => {
  const [isEnableButton, setIsEnableButton] = useState(false);
  const [buttonProperties, setButtonProperties] = useState<
    ButtonProps | undefined
  >(undefined);
  const [extraButtons, setExtraButtons] = useState<JSX.Element[] | undefined>(
    undefined
  );
  const [sectionQuestions, setSectionQuestions] =
    useState<SectionQuestions[]>();
  const [referralsInput, setReferralsInput] =
    useState<VisitDataStatusFilterInput[]>();
  const [growthMonitoring, setGrowthMonitoring] = useState<GrowthMonitoring>();
  const [risk, setRisk] = useState<Risk>(0);
  const [infantInput, setInfantInput] = useState<InfantModelInput>();
  const { isOnline } = useOnlineStatus();

  const { isLoading } = useThunkFetchCall(
    'visits',
    VisitActions.ADD_VISIT_FORM_DATA
  );
  const { isLoading: isLoadingReferral } = useThunkFetchCall(
    'referrals',
    ReferralActions.UPDATE_VISIT_DATA_STATUS
  );
  const { isLoading: isLoadingCreateDocument } = useThunkFetchCall(
    'documents',
    DocumentActions.CREATE_DOCUMENT
  );

  const wasLoading = usePrevious(isLoading);
  const wasLoadingReferral = usePrevious(isLoadingReferral);

  const infantName = useMemo(
    () => infant?.firstName ?? infant?.user?.firstName ?? '',
    [infant]
  );
  const infantCaregiver = useMemo(
    () => infant?.caregiver || infant?.mother?.user,
    [infant]
  );
  const caregiverName = useMemo(
    () => infantCaregiver?.firstName || '',
    [infantCaregiver?.firstName]
  );

  const { visitId } = useParams<InfantProfileParams>();
  const { successDialog } = useRequestResponseDialog();
  const appDispatch = useAppDispatch();
  const dialog = useDialog();
  const history = useHistory();

  const location = useLocation<ViewEditState>();
  const canEdit = location.state?.editView;

  const infantVisits = infant?.visits || [];

  const goHome = useCallback(() => {
    window.sessionStorage.removeItem(currentActivityKey);
    history.push(
      `${ROUTES.CLIENTS.INFANT_PROFILE.ROOT}${
        infant?.userId ?? infant?.user?.id
      }`
    );
  }, [history, infant]);

  const caregiverAgeNotification = useSelector(
    notificationsSelectors.getAllNotifications
  ).find(
    (item) =>
      item?.message?.cta?.includes(
        notificationTagConfig?.InfantCaregiver.cta ?? ''
      ) &&
      item?.message?.routeConfig?.route.includes(
        infant?.userId ?? infant?.user?.id!
      )
  );

  const lowBirthWeightNotification = useSelector(
    notificationsSelectors.getAllNotifications
  ).find(
    (item) =>
      item?.message?.cta?.includes(
        notificationTagConfig?.LowBirthWeight.cta ?? ''
      ) &&
      item?.message?.routeConfig?.route.includes(
        infant?.userId ?? infant?.user?.id!
      )
  );

  const { stepIndex, isRevisit, maxMountedIndex, hasVisitedStep } =
    useVisitedVisitSteps(currentStep, steps);

  const noopSetEnableButton = useCallback((_enabled: boolean) => undefined, []);
  const noopSetButtonProperties = useCallback(
    (_value?: ButtonProps) => undefined,
    []
  );
  const noopSetExtraButtons = useCallback(
    (_buttons?: JSX.Element[]) => undefined,
    []
  );

  const handleSetQuestions = useCallback(
    (value: SectionQuestions[]) => {
      setSectionQuestions((prevSections) => {
        const updatedQuestions = mergeVisitSectionQuestions(
          prevSections,
          value
        ) as SectionQuestions[];

        setSectionQuestionsForm?.(updatedQuestions);
        return updatedQuestions;
      });
    },
    [setSectionQuestionsForm]
  );

  const handleSetReferrals = useCallback(
    (value: VisitDataStatusFilterInput[]) => {
      setReferralsInput((prevState) => {
        const newState = [...(prevState || [])];
        value.forEach((item) => {
          const index = newState.findIndex((element) => element.id === item.id);
          if (index !== -1) {
            newState[index].isCompleted = item.isCompleted;
          } else {
            newState.push(item);
          }
        });
        return newState;
      });
    },
    []
  );

  const handleSetInfant = useCallback((value: InfantModelInput) => {
    setInfantInput?.((prevState) => ({ ...prevState, ...value }));
  }, []);

  const handleGrowthMonitoring = useCallback((value: GrowthMonitoring) => {
    setGrowthMonitoring?.((prevState) => ({ ...prevState, ...value }));
  }, []);

  const handleRisk = useCallback((value: Risk) => {
    setRisk(value);
  }, []);

  const handleOnNext = useCallback(() => {
    const nextIndex = stepIndex + 1;
    setButtonProperties(undefined);
    setExtraButtons(undefined);
    setIsEnableButton(hasVisitedStep(nextIndex));
    onNextStep?.();
  }, [hasVisitedStep, onNextStep, stepIndex]);

  const onSubmit = useCallback(async () => {
    if (!canEdit) {
      return dialog({
        position: DialogPosition.Middle,
        color: 'bg-white',
        render(onClose) {
          return (
            <ActionModal
              className="z-50"
              icon="ExclamationCircleIcon"
              iconColor="alertMain"
              iconClassName="h-10 w-10"
              title="You cannot edit this form"
              detailText="You have completed this visit and can only view this information"
              actionButtons={[
                {
                  text: 'Close',
                  colour: 'primary',
                  type: 'filled',
                  textColour: 'white',
                  leadingIcon: 'XIcon',
                  onClick: onClose,
                },
              ]}
            />
          );
        },
      });
    } else {
      let sections = sectionQuestions?.map((item) => ({
        ...item,
        questions: item.questions.map((question) => ({
          ...question,
          answer: String(question.answer),
        })),
      })) as InputMaybe<Array<InputMaybe<CmsVisitSectionInput>>>;

      // Can we do it like this?
      if (!sectionQuestions && name === 'Pillar 2: Love, talk and play') {
        sections = [
          {
            visitSection: 'Developmental screening',
            questions: [],
          },
        ] as InputMaybe<Array<InputMaybe<CmsVisitSectionInput>>>;
      }
      if (!sectionQuestions && name === 'Pillar 3: Protection') {
        sections = [
          {
            visitSection: 'Immunisations',
            questions: [],
          },
        ] as InputMaybe<Array<InputMaybe<CmsVisitSectionInput>>>;
      }

      const input: CmsVisitDataInputModelInput = {
        visitId,
        infantId: infant?.userId ?? infant?.user?.id,
        visitData: {
          visitName: name,
          sections,
        },
      };

      // Redux updates - update visitAnswers on visit
      const visitAnswers = transformToVisitDataDtos(
        input.visitData!,
        isOnline,
        visitId
      );

      // update the visit state with answers, startDate, attended and sync status
      await appDispatch(
        infantActions.updateInfantVisit({
          infantId: infant?.userId! ?? infant?.user?.id!,
          visitId: visitId,
          visitAnswers: visitAnswers,
          visitName: name!,
          isOnline: isOnline,
        })
      );

      // when offline, populate infant status info with data we have
      if (!isOnline) {
        const updatedStatusInfo = await getInfantStatusInfo(infantVisits);

        const updatedInfant = {
          ...infant,
          statusInfo: updatedStatusInfo,
        };

        await appDispatch(infantActions.updateInfantStatusInfo(updatedInfant));
      }

      // update the infant status for answering no to csg
      if (input.visitData?.visitName === 'Pillar 5: Extra care') {
        const childDoc = input.visitData.sections?.find(
          (section) => section?.visitSection === 'Child documentation'
        );
        if (childDoc) {
          const csgAnswer = childDoc?.questions?.find(
            (question) =>
              question?.question === 'Is {client} receiving the CSG?'
          );
          if (csgAnswer) {
            appDispatch(
              infantActions.updateInfantCsgStatus({
                infantId: input.infantId!,
                hasAnsweredNoToCSGReceiving: csgAnswer?.answer === 'false',
              })
            );
          }
        }
      }

      const referrals = referralsInput?.map((item) => ({
        ...item,
        isCompleted: item.isCompleted,
      })) as VisitDataStatusFilterInput[];

      if (!!referrals?.length && isOnline) {
        appDispatch(
          referralThunkActions.updateVisitDataStatus({ input: referrals })
        );
      }

      if (isOnline) {
        await appDispatch(visitThunkActions.addVisitFormData(input));
      }

      if (caregiverAgeNotification) {
        appDispatch(
          notificationActions.removeNotification(caregiverAgeNotification!)
        );
      }
      if (lowBirthWeightNotification) {
        appDispatch(
          notificationActions.removeNotification(lowBirthWeightNotification!)
        );
      }

      //------------------------
      // not sure about these calls
      appDispatch(
        visitActions.addCompletedVisitsByVisitId({
          visitId,
          visits: [name || ''],
        })
      );

      if (!!infantInput && infant?.user?.id && isOnline) {
        appDispatch(
          infantThunkActions.updateInfant({
            input: infantInput,
            id: infant?.userId ?? infant?.user?.id,
          })
        ).unwrap();
      }

      //------------------------

      onClose?.();
    }
  }, [
    canEdit,
    dialog,
    sectionQuestions,
    visitId,
    infant?.userId,
    infant?.user?.id,
    name,
    referralsInput,
    appDispatch,
    caregiverAgeNotification,
    lowBirthWeightNotification,
    infantInput,
    isOnline,
    infantVisits,
  ]);

  useLayoutEffect(() => {
    setButtonProperties(undefined);
    setExtraButtons(undefined);
    if (isRevisit) {
      setIsEnableButton(true);
    }
  }, [isRevisit, stepIndex]);

  const renderContent = useMemo(() => {
    if (!steps?.length) return;

    return steps.map((StepComponent, index) => {
      if (index > maxMountedIndex || !StepComponent) return null;

      const isActive = index === stepIndex;

      return (
        <div key={getVisitStepKey(StepComponent, index)} hidden={!isActive}>
          <StepComponent
            infant={infant}
            isTipPage={isActive && isTipPage}
            setIsTip={setIsTip}
            growthMonitoring={growthMonitoring}
            sectionQuestions={sectionQuestions}
            setSectionQuestions={handleSetQuestions}
            setGrowthMonitoring={handleGrowthMonitoring}
            setReferralsInput={handleSetReferrals}
            setEnableButton={isActive ? setIsEnableButton : noopSetEnableButton}
            setButtonProperties={
              isActive ? setButtonProperties : noopSetButtonProperties
            }
            setExtraButtons={isActive ? setExtraButtons : noopSetExtraButtons}
            onNextStep={onNextStep}
            onSubmit={onSubmit}
            setRisk={handleRisk}
            setInfantInput={handleSetInfant}
          />
        </div>
      );
    });
  }, [
    growthMonitoring,
    handleGrowthMonitoring,
    handleSetQuestions,
    handleSetReferrals,
    handleSetInfant,
    infant,
    isTipPage,
    maxMountedIndex,
    noopSetButtonProperties,
    noopSetEnableButton,
    noopSetExtraButtons,
    onNextStep,
    onSubmit,
    sectionQuestions,
    setIsTip,
    stepIndex,
    steps,
    handleRisk,
  ]);

  const renderButton = useMemo(() => {
    if (
      Number(currentStep) === 0 &&
      !(Number(currentStep) <= Number(steps?.length) - 1)
    ) {
      return {
        action: handleOnNext,
        text: 'Start',
        icon: 'ClipboardListIcon',
      };
    }

    if (Number(steps?.length) === 1) {
      return {
        action: onSubmit,
        text: 'Save',
        icon: 'SaveIcon',
      };
    }
    if (Number(currentStep) === 0) {
      return {
        action: handleOnNext,
        text: name?.startsWith('Care for') ? 'Start' : 'Next',
        icon: 'ClipboardListIcon',
      };
    }

    if (Number(currentStep) < Number(steps?.length) - 1) {
      return {
        action: handleOnNext,
        text: 'Next',
        icon: 'ArrowCircleRightIcon',
      };
    }

    return {
      action: onSubmit,
      text: risk === 2 ? 'Close' : buttonProperties?.text || 'Save',
      icon: risk === 2 ? 'XIcon' : 'SaveIcon',
    };
  }, [
    risk,
    currentStep,
    handleOnNext,
    onSubmit,
    steps?.length,
    name,
    buttonProperties,
  ]);

  useEffect(() => {
    if (
      (wasLoading && !isLoading) ||
      (wasLoadingReferral && !isLoadingReferral)
    ) {
      if (risk === 1) {
        dialog({
          blocking: false,
          position: DialogPosition.Middle,
          color: 'bg-white',
          render: (onClose) => {
            return (
              <ActionModal
                icon={'ExclamationCircleIcon'}
                iconColor="errorMain"
                iconBorderColor="errorBg"
                className="z-50"
                importantText={`Take ${infantName} to the hospital immediately or call an ambulance!`}
                detailText={`${infantName} is not well and needs urgent care!`}
                actionButtons={[
                  {
                    colour: 'primary',
                    text: 'Close',
                    textColour: 'primary',
                    type: 'outlined',
                    leadingIcon: 'XIcon',
                    onClick: onClose,
                  },
                ]}
              />
            );
          },
        });
        goHome?.();
      } else if (risk === 2) {
        goHome();
      } else {
        onClose?.();
      }
    }
  }, [
    isLoading,
    isLoadingReferral,
    onClose,
    successDialog,
    wasLoading,
    wasLoadingReferral,
    dialog,
    risk,
    infantName,
    goHome,
    caregiverName,
    name,
    history,
    infant?.userId,
    infant?.user?.id,
  ]);

  const renderExtraButtons = useCallback(() => {
    if (!extraButtons || extraButtons.length === 0) return null;
    return extraButtons.map((el, index) => el);
  }, [extraButtons]);

  return (
    <div className="flex h-full flex-col">
      {renderContent}
      {!isTipPage && (
        <div id="button" className="mx-4 mt-auto flex flex-col items-end">
          {renderExtraButtons()}
          <Button
            type={buttonProperties?.type || 'filled'}
            color={buttonProperties?.color || 'primary'}
            textColor={buttonProperties?.textColor || 'white'}
            icon={renderButton.icon}
            className="mb-4 w-full"
            text={renderButton.text}
            onClick={renderButton.action}
            disabled={!isEnableButton || isLoading || isLoadingCreateDocument}
            isLoading={isLoading || isLoadingCreateDocument}
          />
        </div>
      )}
    </div>
  );
};
